/**
 * Product-type scoping for book retailers.
 *
 * Retailers carry a `productType` ('ebook' | 'audio' | 'print') describing what
 * they sell, so a title is only ever offered the retailers that can actually
 * carry it. Ebook is the default everywhere: it is what retailers stored before
 * the field existed are, and it matches how book search already defaults (see
 * `getBookProductTypeQueryMatch` in orion-step).
 */

import { PRODUCT_TYPES } from 'src/constants/const';

export const BOOK_PRODUCT_TYPE_EBOOK = PRODUCT_TYPES.EBOOK;
export const BOOK_PRODUCT_TYPE_AUDIO = PRODUCT_TYPES.AUDIO;

/** Allowed product types, in the order they are offered to users. */
export const BOOK_PRODUCT_TYPES = Object.values(PRODUCT_TYPES);

const VALID_PRODUCT_TYPES = new Set(BOOK_PRODUCT_TYPES);

/** Normalize a product type, falling back to ebook for empty/unknown values. */
export const normalizeProductType = productType => {
  const normalized = String(productType || '')
    .trim()
    .toLowerCase();
  return VALID_PRODUCT_TYPES.has(normalized) ? normalized : BOOK_PRODUCT_TYPE_EBOOK;
};

/**
 * Retailers that sell `productType`, preserving input order.
 *
 * Kept permissive on purpose:
 * - a retailer with no `productType` is treated as ebook, so partially migrated
 *   data degrades to today's behaviour instead of an empty dropdown;
 * - the synthetic 'ALL' option (and anything else without a real retailer
 *   product type) is always kept, since it is a control rather than a retailer.
 */
export const filterRetailersByProductType = (options, productType) => {
  if (!Array.isArray(options)) return [];
  const wanted = normalizeProductType(productType);

  return options.filter(option => {
    if (!option) return false;
    // 'ALL' is a select-all control, not a retailer — never filter it out.
    if (String(option.value).toLowerCase() === 'all') return true;
    return normalizeProductType(option.productType) === wanted;
  });
};

/**
 * Retailers that sell ANY of `productTypes`, de-duplicated by `value` and
 * preserving input order.
 *
 * For bulk actions over a mixed selection: updating an ebook and an audio title
 * together has to offer the union of both lists, otherwise the audio titles could
 * only ever be given ebook stores. A single-type selection collapses to exactly
 * the same list `filterRetailersByProductType` would return.
 *
 * De-duplication is by `value` because one retailer can legitimately appear under
 * more than one product type (Amazon sells both ebooks and audiobooks), and the
 * dropdown must not show it twice.
 *
 * An empty/missing `productTypes` falls back to ebook, matching
 * `normalizeProductType` — callers with no selection behave as they did before.
 */
export const filterRetailersByProductTypes = (options, productTypes) => {
  if (!Array.isArray(options)) return [];

  const wanted = new Set(
    (Array.isArray(productTypes) ? productTypes : [productTypes]).filter(Boolean).map(normalizeProductType)
  );
  if (wanted.size === 0) wanted.add(BOOK_PRODUCT_TYPE_EBOOK);

  return dedupeRetailerOptions(
    options.filter(option => {
      if (!option) return false;
      // 'ALL' is a select-all control, not a retailer — never filter it out.
      const isAll = String(option.value).toLowerCase() === 'all';
      return isAll || wanted.has(normalizeProductType(option.productType));
    })
  );
};

/**
 * De-duplicate retailer options by `value` (case-insensitive), preserving order.
 *
 * A retailer can legitimately appear more than once in a raw options list (e.g.
 * once per product type it sells, as with Amazon selling both ebooks and
 * audiobooks) — this collapses those to a single entry.
 */
export const dedupeRetailerOptions = options => {
  if (!Array.isArray(options)) return [];
  const seen = new Set();
  return options.filter(option => {
    if (!option) return false;
    const key = String(option.value).toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

/**
 * Unique, normalized product types present in a set of selected titles.
 *
 * Feeds `productTypes` on RetailerSelect for bulk actions. Titles with no
 * `productType` recorded normalize to ebook, which is what an unset value has
 * always meant here.
 */
export const getSelectedProductTypes = (titles, key = 'productType') => {
  if (!Array.isArray(titles)) return [];
  return [...new Set(titles.filter(Boolean).map(title => normalizeProductType(title?.[key])))];
};
